import { CalendarIcon } from "@radix-ui/react-icons";
import { addDays, format } from "date-fns";
import { DateRange, SelectRangeEventHandler } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export const datePickerPresets = {
  Today: {
    from: addDays(new Date(), -1),
    to: new Date(),
  },
  "Last 7 days": {
    from: addDays(new Date(), -7),
    to: new Date(),
  },
  "Last 30 days": {
    from: addDays(new Date(), -30),
    to: new Date(),
  },
  "All Time": {
    from: new Date(0),
    to: new Date(),
  },
} as const;

export type DatePickerPreset = keyof typeof datePickerPresets;

export type Props = {
  date: DateRange | undefined;
  setDate: (date: DateRange | undefined) => void;
} & React.HTMLAttributes<HTMLDivElement>;

export function DatePickerWithRange({ className, date, setDate }: Props) {
  const handleSelect: SelectRangeEventHandler = (newDate, selectedDay) => {
    const isRangeAlreadySelected = date?.from && date?.to;
    if (isRangeAlreadySelected) {
      setDate({ from: selectedDay, to: undefined });
      return;
    }
    setDate(newDate);
  };
  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left",
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="flex w-auto flex-col space-y-2 p-2"
        >
          <Select
            onValueChange={(value: DatePickerPreset) =>
              setDate(datePickerPresets[value])
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent position="popper">
              {Object.keys(datePickerPresets).map((key) => (
                <SelectItem key={key} value={key}>
                  {key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleSelect}
            numberOfMonths={2}
            toDate={new Date()}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
